import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, MessageSquare, Bug, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import IssueDetailDialog from "@/components/IssueDetailDialog";

interface Issue {
  id: string;
  title: string;
  description: string;
  issue_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  profile_id: string;
  client_id: string;
  profiles?: { full_name: string | null } | null;
  _comment_count?: number;
}

const ISSUE_TYPES = [
  { value: "all", label: "All Types" },
  { value: "bug", label: "Bug" },
  { value: "feature_request", label: "Feature Request" },
  { value: "data_syncing", label: "Data Syncing" },
  { value: "custom_help", label: "Custom Help" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "rejected", label: "Rejected" },
];

const statusColor: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const typeLabel: Record<string, string> = {
  bug: "Bug",
  feature_request: "Feature Request",
  data_syncing: "Data Syncing",
  custom_help: "Custom Help",
  other: "Other",
};

const AdminIssues = () => {
  const { profile } = useAuth();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchIssues = async () => {
    if (!profile?.client_id) return;
    setLoading(true);

    let query = supabase
      .from("issues" as any)
      .select("id, title, description, issue_type, status, created_at, updated_at, profile_id, client_id, profiles(full_name)")
      .eq("client_id", profile.client_id)
      .order("created_at", { ascending: false });

    const { data } = await query;
    const issuesData = (data as any[]) || [];

    // Fetch comment counts
    if (issuesData.length > 0) {
      const ids = issuesData.map((i: any) => i.id);
      const { data: commentCounts } = await supabase
        .from("issue_comments" as any)
        .select("issue_id")
        .in("issue_id", ids);
      const countMap = new Map<string, number>();
      (commentCounts || []).forEach((c: any) => {
        countMap.set(c.issue_id, (countMap.get(c.issue_id) || 0) + 1);
      });
      issuesData.forEach((issue: any) => {
        issue._comment_count = countMap.get(issue.id) || 0;
      });
    }

    setIssues(issuesData);
    setLoading(false);
  };

  useEffect(() => {
    fetchIssues();
  }, [profile?.client_id]);

  const handleStatusChange = (issueId: string, newStatus: string) => {
    setIssues((prev) =>
      prev.map((i) => (i.id === issueId ? { ...i, status: newStatus } : i))
    );
    if (selectedIssue?.id === issueId) {
      setSelectedIssue((prev) => prev ? { ...prev, status: newStatus } : prev);
    }
  };

  const filtered = issues.filter((i) => {
    if (typeFilter !== "all" && i.issue_type !== typeFilter) return false;
    if (statusFilter !== "all" && i.status !== statusFilter) return false;
    if (searchQuery && !i.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const openCount = issues.filter((i) => i.status === "open").length;

  return (
    <div className="flex flex-1 flex-col gap-4 p-6 overflow-auto">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <AlertCircle className="h-6 w-6" /> Reported Issues
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          View and manage issues reported by users in your organization.
          {openCount > 0 && <> <Badge variant="secondary" className="ml-1">{openCount} open</Badge></>}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ISSUE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent">
            <Bug className="h-7 w-7 text-accent-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">
            {issues.length === 0 ? "No issues reported yet." : "No issues match your filters."}
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2 text-left font-medium">Title</th>
                <th className="px-4 py-2 text-left font-medium">Reported By</th>
                <th className="px-4 py-2 text-left font-medium">Type</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Date</th>
                <th className="px-4 py-2 text-right font-medium">Comments</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((issue) => (
                <tr
                  key={issue.id}
                  className="border-b last:border-0 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => { setSelectedIssue(issue); setDetailOpen(true); }}
                >
                  <td className="px-4 py-2 font-medium">{issue.title}</td>
                  <td className="px-4 py-2 text-muted-foreground">{issue.profiles?.full_name || "Unknown"}</td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="text-xs">{typeLabel[issue.issue_type] || issue.issue_type}</Badge>
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className={`text-xs ${statusColor[issue.status] || ""}`}>{issue.status}</Badge>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {format(new Date(issue.created_at), "MMM d, yyyy")}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {(issue._comment_count || 0) > 0 && (
                      <span className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                        <MessageSquare className="h-3 w-3" />
                        {issue._comment_count}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IssueDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        issue={selectedIssue}
        onBack={() => setDetailOpen(false)}
        canManage
        onStatusChange={handleStatusChange}
      />
    </div>
  );
};

export default AdminIssues;
